"use client";
import React from "react";
import { Merchant } from "@/dto/merchant";
import { ShoppingBagIcon } from "@/components/_icons/shoppingBagIcon";
import CloseIcon from "@/components/_icons/closeIcon";
import { getFromLocalStorage } from "@/utils/localStorageUtils";
import { PrimaryButton } from "@/components/button/primaryButton";
import { TextButton } from "@/components/button/textButton";
import { lightenColor } from "@/utils/lightenColor";
import { useRouter } from "next13-progressbar";
import { goToGuestCheckout } from "@/utils/scanToOrderUtils";
import { ScanToOrderInfo } from "@/dto/scanToOrderInfo";
import { LocalStorageKey } from "@/utils/storageKeys";
import { RegularModal } from "@/components/modals/modals";
import { useCurrency } from "@/app/providers/currencyContext";
import { cloverColorTheme } from "@/utils/cloverColorTheme";

export const ScanToOrderKitchenModal = ({
  isOpen,
  onRequestClose,
  merchant,
  scanToOrderInfo,
}: {
  isOpen: boolean;
  onRequestClose: () => void;
  merchant: Merchant;
  scanToOrderInfo?: ScanToOrderInfo;
}) => {
  const router = useRouter();
  const merchantColor =
    merchant?.merchantMeta?.colorPalette?.color ||
    cloverColorTheme.Main.primaryColor;
  const tableData = getFromLocalStorage<any>(LocalStorageKey.TABLE_DATA, {});
  const tableItems = tableData?.order?.cart?.items;

  const { format } = useCurrency();

  return (
    <RegularModal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      overlayClassName="bg-opacity-75"
    >
      <div className="bg-white sm:rounded-lg shadow-lg w-full h-full sm:h-[auto] sm:w-[60%] mx-auto overflow-y-auto sm:max-w-[670px] sm:max-h-[90vh]">
        <div className="flex flex-col items-center justify-center pt-8 pb-4 px-4 mt-16 sm:mt-0">
          <button
            onClick={onRequestClose}
            className="absolute top-4 right-4 text-gray-600 hover:text-gray-900 z-10"
          >
            <CloseIcon />
          </button>
          <ShoppingBagIcon brandingColor={merchantColor} />
          <h1 className="font-medium text-[32px] text-[#373737] mt-4">
            Hooray!
          </h1>
          <p className="font-normal text-[15px] text-[#373737] mt-2 mb-2 text-center">
            Your order has been sent to the kitchen and will be brought to your
            table soon.
          </p>
          {tableItems?.length > 0 && (
            <div className="w-full pb-32 sm:pb-0">
              {tableItems.map((item: any, index: any) => (
                <div
                  key={index}
                  className="flex flex-col justify-between items-center pt-4 pb-8 border-b"
                >
                  <div className="w-full flex flex-row justify-between">
                    <div className="w-full flex flex-col justify-between">
                      <p className="text-lg text-[#373737] max-w-[310px]">
                        {item?.name}&nbsp;
                        {item?.quantity > 1 && `× ` + item?.quantity}
                      </p>
                      <div className="max-w-[80%]">
                        {item?.modifiers.length > 0 && (
                          <p className="text-[15px] text-[#727272]">
                            {item?.modifiers
                              .map((modifier: any) => modifier.name)
                              .join(", ")}
                          </p>
                        )}
                        <div className="w-full">
                          <p className="text-[15px] text-[#727272] max-w-[310px] break-words">
                            {item?.specialInstructions
                              ? `"${item?.specialInstructions}"`
                              : ""}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col justify-start items-end">
                      <p className="text-[14px] font-medium mt-0.5">
                        {format(item?.price)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="absolute sm:relative bottom-0 left-0 w-full sm:mt-4 flex flex-col-reverse sm:flex-col sm:gap-4 items-center">
            <PrimaryButton
              style={{
                backgroundColor: merchantColor,
              }}
              onClick={onRequestClose}
              className="w-full text-white sm:w-[320px] h-[60px] rounded-none"
            >
              <p className="font-medium text-[20px]">Add more to your order</p>
            </PrimaryButton>
            <TextButton
              className="w-full sm:w-[320px] h-[60px] rounded-none text-[#373737]"
              onClick={() => goToGuestCheckout(router, scanToOrderInfo)}
              style={{
                color: "#373737",
                backgroundColor: lightenColor(merchantColor, 90),
              }}
            >
              <p className="font-medium text-[20px]">Close out / Pay</p>
            </TextButton>
          </div>
        </div>
      </div>
    </RegularModal>
  );
};
